// Business logic validation for step management
const STEP_HIERARCHY = {
  'NO_CANDIDATE': 0,
  'CANDIDATE_IDENTIFIED': 1,
  'PUSH': 1.5,              // NEW: Between identified and sent
  'CANDIDATE_SENT': 2,
  'CANDIDATE_INTERVIEW': 3,
  'GO_NOGO': 4,
  'SALE': 5,
  'FROZEN': 0,              // NEW: Can be set at any time, doesn't progress
  'CANCELED': 5,
  'DELETED': 6
};

export class StepValidationError extends Error {
  constructor(message, code) {
    super(message);
    this.name = 'StepValidationError';
    this.code = code;
  }
}

export function getNextAllowedSteps(existingSteps, userRole) {
  // If no existing steps, only CANDIDATE_IDENTIFIED is allowed
  if (!existingSteps || existingSteps.length === 0) {
    return ['CANDIDATE_IDENTIFIED'];
  }

  // Get existing step types to avoid duplicates
  const existingTypes = existingSteps.map(step => step.type);

  // Special handling for FROZEN - can be added at any time
  const nextSteps = ['FROZEN'];
  
  for (const stepType of Object.keys(STEP_HIERARCHY)) {
    // Skip NO_CANDIDATE and DELETED
    if (stepType === 'NO_CANDIDATE' || stepType === 'DELETED') continue;
    
    // Skip if step already exists (no duplicates)
    if (existingTypes.includes(stepType)) continue;
    
    // Allow all other steps
    nextSteps.push(stepType);
  }

  return nextSteps;
}

export function validateStepProgression(existingSteps, newStepType, userRole = null) {
  // FROZEN can be added at any time
  if (newStepType === 'FROZEN') {
    return;
  }
  
  // PUSH can be added after CANDIDATE_IDENTIFIED
  if (newStepType === 'PUSH') {
    const hasIdentified = existingSteps.some(step => step.type === 'CANDIDATE_IDENTIFIED');
    if (!hasIdentified) {
      throw new StepValidationError(
        'PUSH step requires CANDIDATE_IDENTIFIED step first',
        'INVALID_FIRST_STEP'
      );
    }
    return;
  }

  // Validate that linked candidates start with CANDIDATE_IDENTIFIED
  if (!existingSteps || existingSteps.length === 0) {
    if (newStepType !== 'CANDIDATE_IDENTIFIED') {
      throw new StepValidationError(
        'First step for a linked candidate must be CANDIDATE_IDENTIFIED',
        'INVALID_FIRST_STEP'
      );
    }
    return;
  }

  // ✅ SIMPLIFIED: Just check if step is allowed (no role restrictions)
  const allowedSteps = getNextAllowedSteps(existingSteps, userRole);
  if (!allowedSteps.includes(newStepType)) {
    throw new StepValidationError(
      `Step ${newStepType} is not allowed at this stage. Allowed: ${allowedSteps.join(', ')}`,
      'STEP_NOT_ALLOWED'
    );
  }
}

export const validateNoDuplicateSteps = (existingSteps, newStepType) => {
  const duplicateStep = existingSteps.find(step => step.type === newStepType);
  if (duplicateStep) {
    throw new StepValidationError(
      `Step type ${newStepType} already exists for this candidate`,
      'DUPLICATE_STEP'
    );
  }
};

export const validateStepDates = (stepType, stepData) => {
  const now = new Date();
  
  switch (stepType) {
    case 'CANDIDATE_SENT':
      if (new Date(stepData.mailSentDate) > now) {
        throw new StepValidationError(
          'Mail sent date cannot be in the future',
          'INVALID_DATE'
        );
      }
      if (new Date(stepData.followUpDate) <= new Date(stepData.mailSentDate)) {
        throw new StepValidationError(
          'Follow-up date must be after mail sent date',
          'INVALID_DATE_SEQUENCE'
        );
      }
      break;
      
    case 'CANDIDATE_INTERVIEW':
      if (new Date(stepData.invitationDate) > new Date(stepData.interviewDate)) {
        throw new StepValidationError(
          'Interview date must be after invitation date',
          'INVALID_DATE_SEQUENCE'
        );
      }
      if (new Date(stepData.interviewDate) < now.setHours(0, 0, 0, 0)) {
        // Allow past interviews but warn
        console.warn('Interview date is in the past');
      }
      break;
      
    case 'GO_NOGO':
      if (stepData.projectedStartDate && new Date(stepData.projectedStartDate) < now) {
        throw new StepValidationError(
          'Projected start date cannot be in the past',
          'INVALID_DATE'
        );
      }
      if (stepData.endDate && stepData.projectedStartDate && 
          new Date(stepData.endDate) <= new Date(stepData.projectedStartDate)) {
        throw new StepValidationError(
          'End date must be after start date',
          'INVALID_DATE_SEQUENCE'
        );
      }
      break;
      
    case 'SALE':
      if (new Date(stepData.endDate) <= new Date(stepData.startDate)) {
        throw new StepValidationError(
          'End date must be after start date',
          'INVALID_DATE_SEQUENCE'
        );
      }
      break;
  }
};
